/* Peppers Hero Slider – dezente Lichtpartikel (Pollen in Abendsonne)
   Rein dekorativ: startet erst nach dem Laden der Seite, pausiert außerhalb des Viewports und bei
   verstecktem Tab, wird bei prefers-reduced-motion gar nicht erzeugt. Fehlen benötigte APIs,
   unterbleibt der Effekt still. */

if (!customElements.get('peppers-hero-particles')) {
  const PARTICLE_COUNTS = {
    subtle: { desktop: 26, mobile: 12 },
    normal: { desktop: 44, mobile: 20 },
  };
  const SPEED_FACTORS = { slow: 0.6, normal: 1 };
  const MAX_DPR = 1.5;
  const MOBILE_FRAME_INTERVAL = 1000 / 30;
  const SPRITE_SIZE = 64;

  const random = (min, max) => min + Math.random() * (max - min);

  class PeppersHeroParticles extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener(
        'change',
        () => (this.reducedMotion.matches ? this.teardown() : this.whenIdle()),
        { signal: this.abortController.signal }
      );
      if (!this.reducedMotion.matches) this.whenIdle();
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
      this.cancelIdle();
      this.teardown();
    }

    whenIdle() {
      const start = () => {
        this.cancelIdle();
        if ('requestIdleCallback' in window) {
          this.idleHandle = window.requestIdleCallback(() => this.setup(), { timeout: 2000 });
        } else {
          this.idleTimeout = window.setTimeout(() => this.setup(), 200);
        }
      };

      if (document.readyState === 'complete') {
        start();
      } else {
        window.addEventListener('load', start, { once: true, signal: this.abortController.signal });
      }
    }

    cancelIdle() {
      if (this.idleHandle && 'cancelIdleCallback' in window) window.cancelIdleCallback(this.idleHandle);
      clearTimeout(this.idleTimeout);
      this.idleHandle = null;
      this.idleTimeout = null;
    }

    setup() {
      if (this.canvas || this.reducedMotion.matches) return;
      if (!('IntersectionObserver' in window) || !('ResizeObserver' in window)) return;

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) return;

      this.canvas = canvas;
      this.context = context;
      this.appendChild(canvas);

      this.sprite = this.createSprite(getComputedStyle(this).getPropertyValue('--phs-particle-color').trim());
      if (!this.sprite) {
        this.teardown();
        return;
      }

      this.speedFactor = SPEED_FACTORS[this.dataset.speed] || SPEED_FACTORS.slow;
      this.intersecting = false;
      this.frame = null;
      this.lastTime = 0;
      this.lastDraw = 0;
      this.loop = this.loop.bind(this);
      this.particleSignal = new AbortController();

      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(this);

      this.intersectionObserver = new IntersectionObserver((entries) => {
        this.intersecting = entries[entries.length - 1].isIntersecting;
        this.updateRunning();
      });
      this.intersectionObserver.observe(this);

      document.addEventListener('visibilitychange', () => this.updateRunning(), {
        signal: this.particleSignal.signal,
      });
    }

    teardown() {
      this.stop();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
      if (this.particleSignal) this.particleSignal.abort();
      if (this.canvas) this.canvas.remove();
      this.resizeObserver = null;
      this.intersectionObserver = null;
      this.particleSignal = null;
      this.canvas = null;
      this.context = null;
      this.particles = null;
    }

    /* Weicher, warmer Lichtpunkt – einmalig vorgerendert, danach nur noch drawImage. */
    createSprite(color) {
      let sprite;
      if (typeof OffscreenCanvas === 'function') {
        try {
          sprite = new OffscreenCanvas(SPRITE_SIZE, SPRITE_SIZE);
        } catch (error) {
          sprite = null;
        }
      }
      if (!sprite) {
        sprite = document.createElement('canvas');
        sprite.width = SPRITE_SIZE;
        sprite.height = SPRITE_SIZE;
      }

      const context = sprite.getContext('2d');
      if (!context) return null;

      context.fillStyle = color || '#ffd89a';
      const [r, g, b] = this.parseHex(context.fillStyle);
      const center = SPRITE_SIZE / 2;
      const gradient = context.createRadialGradient(center, center, 0, center, center, center);
      gradient.addColorStop(0, 'rgba(255, 250, 235, 1)');
      gradient.addColorStop(0.2, `rgba(${r}, ${g}, ${b}, 0.95)`);
      gradient.addColorStop(0.5, `rgba(${r}, ${g}, ${b}, 0.35)`);
      gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);
      context.fillStyle = gradient;
      context.fillRect(0, 0, SPRITE_SIZE, SPRITE_SIZE);
      return sprite;
    }

    parseHex(value) {
      const match = /^#([0-9a-f]{6})$/i.exec(value);
      if (!match) return [255, 216, 154];
      const number = parseInt(match[1], 16);
      return [(number >> 16) & 255, (number >> 8) & 255, number & 255];
    }

    resize() {
      if (!this.canvas) return;
      const width = this.clientWidth;
      const height = this.clientHeight;
      if (!width || !height) return;

      const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
      this.canvas.width = Math.round(width * dpr);
      this.canvas.height = Math.round(height * dpr);
      this.context.setTransform(dpr, 0, 0, dpr, 0, 0);

      this.width = width;
      this.height = height;
      this.isMobile = width < 750;

      const counts = PARTICLE_COUNTS[this.dataset.intensity] || PARTICLE_COUNTS.subtle;
      const count = this.isMobile ? counts.mobile : counts.desktop;
      if (!this.particles || this.particles.length !== count) {
        this.particles = Array.from({ length: count }, () => this.createParticle(true));
      }

      if (!this.frame) this.draw(0);
    }

    createParticle(initial) {
      return {
        x: random(0, this.width),
        y: initial ? random(0, this.height) : this.height + random(4, 24),
        radius: random(1, 3.2),
        alpha: random(0.4, 0.8),
        rise: random(4, 12),
        drift: random(-3, 3),
        swayAmplitude: random(6, 18),
        swayFrequency: random(0.08, 0.22),
        pulseFrequency: random(0.3, 0.8),
        phase: random(0, Math.PI * 2),
        glow: 0,
        nextGlow: random(1, 8),
      };
    }

    updateRunning() {
      if (this.canvas && this.intersecting && !document.hidden) {
        this.start();
      } else {
        this.stop();
      }
    }

    start() {
      if (this.frame) return;
      this.lastTime = 0;
      this.frame = requestAnimationFrame(this.loop);
    }

    stop() {
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
    }

    loop(time) {
      this.frame = requestAnimationFrame(this.loop);
      if (this.isMobile && time - this.lastDraw < MOBILE_FRAME_INTERVAL) return;
      this.lastDraw = time;

      const delta = this.lastTime ? Math.min((time - this.lastTime) / 1000, 0.1) : 0;
      this.lastTime = time;
      this.update(delta);
      this.draw(time / 1000);
    }

    update(delta) {
      if (!this.particles) return;
      const speed = this.speedFactor;

      this.particles.forEach((particle, index) => {
        particle.y -= particle.rise * speed * delta;
        particle.x += particle.drift * speed * delta;

        particle.nextGlow -= delta;
        if (particle.nextGlow <= 0 && particle.glow === 0) {
          particle.glow = 0.0001;
          particle.nextGlow = random(4, 10);
        }
        if (particle.glow > 0) {
          particle.glow += delta / 1.6;
          if (particle.glow >= 1) particle.glow = 0;
        }

        if (particle.y < -24 || particle.x < -24 || particle.x > this.width + 24) {
          this.particles[index] = this.createParticle(false);
        }
      });
    }

    draw(seconds) {
      const context = this.context;
      if (!context || !this.particles) return;

      context.clearRect(0, 0, this.width, this.height);
      context.globalCompositeOperation = 'lighter';

      this.particles.forEach((particle) => {
        const sway = Math.sin(seconds * particle.swayFrequency * Math.PI * 2 + particle.phase) * particle.swayAmplitude;
        const pulse = 0.7 + 0.3 * Math.sin(seconds * particle.pulseFrequency * Math.PI * 2 + particle.phase);
        const glow = particle.glow > 0 ? Math.sin(particle.glow * Math.PI) : 0;
        const size = particle.radius * (4.5 + glow * 4);

        context.globalAlpha = Math.min(1, particle.alpha * pulse + glow * 0.5);
        context.drawImage(this.sprite, particle.x + sway - size, particle.y - size, size * 2, size * 2);
      });

      context.globalAlpha = 1;
      context.globalCompositeOperation = 'source-over';
    }
  }

  customElements.define('peppers-hero-particles', PeppersHeroParticles);
}
